// CppWorkspace — the CPP / PCI section-by-section editor. Loads one record,
// renders the left-hand section nav + right-hand section editor, and exposes
// record-level metadata (status, dates, sign-off names) + branded PDF export.
// Phase 2: formal sign-off workflow (send for review / approve / reopen / issue)
// with record locking once Approved, a status-history "Activity" panel, and a
// "Missing information" strip so gaps are visible without reading every section.
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Share2, CheckCircle2, Lock, ClipboardCheck, RotateCcw, Send, History, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, filenameFromResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pill from "@/components/Pill";
import CppSectionNav from "@/components/cpp/CppSectionNav";
import CppSectionEditor from "@/components/cpp/CppSectionEditor";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);
const DOC_LABEL = { cpp: "Construction Phase Plan", pci: "Pre-Construction Information" };
const EDITABLE_STATUSES = new Set(["Draft", "In progress"]);
const LOCKED_STATUSES = new Set(["Approved", "Issued"]);
const PUBLISHABLE_STATUSES = new Set(["Approved", "Issued"]);
const STATUS_TONE = { Draft: "neutral", "In progress": "amber", "Ready for review": "blue", Approved: "green", Issued: "green" };
const inputCls = "h-8 px-2.5 rounded-lg border border-[#E5E0DA] bg-white text-[12.5px]";

export default function CppWorkspace() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const [record, setRecord] = useState(null);
    const [nav, setNav] = useState([]);
    const [activeCode, setActiveCode] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [workflowBusy, setWorkflowBusy] = useState(false);
    const [numbering, setNumbering] = useState({});
    const [reopenPrompt, setReopenPrompt] = useState(false);
    const [reopenNote, setReopenNote] = useState("");
    const [activityOpen, setActivityOpen] = useState(false);
    const [missingOpen, setMissingOpen] = useState(true);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/cpp/${id}`);
            setRecord(data);
            setNav(data.nav || []);
            setNumbering(Object.fromEntries((data.nav || []).map((n) => [n.code, n.number])));
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load record"); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (nav.length && !activeCode) {
            const first = nav.find((n) => n.kind === "numbered" && !n.is_container) || nav[0];
            setActiveCode(first?.code);
        }
    }, [nav, activeCode]);

    const updateMeta = async (patch) => {
        try {
            const { data } = await api.patch(`/cpp/${id}`, patch);
            setRecord((r) => ({ ...r, ...data }));
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    };

    const onSectionSaved = (sections) => {
        setRecord((r) => ({ ...r, sections }));
        setNav((n) => n.map((item) => {
            const s = sections.find((x) => x.code === item.code);
            return s ? { ...item, not_applicable: s.not_applicable } : item;
        }));
    };

    const exportPdf = async () => {
        setExporting(true);
        try {
            const res = await api.get(`/cpp/${id}/export/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filenameFromResponse(res, `${record?.reference || "document"}.pdf`);
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("PDF export failed"); }
        setExporting(false);
    };

    const publish = async () => {
        setPublishing(true);
        try {
            const { data } = await api.post(`/cpp/${id}/publish`);
            setRecord((r) => ({ ...r, ...data.record }));
            toast.success(data.message);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Publish failed"); }
        setPublishing(false);
    };

    const runWorkflow = async (action, body) => {
        setWorkflowBusy(true);
        try {
            const { data } = await api.post(`/cpp/${id}/${action}`, body);
            const rec = data.record || data;
            setRecord((r) => ({ ...r, ...rec }));
            if (data.message) toast.success(data.message);
            else toast.success("Status updated");
            setReopenPrompt(false); setReopenNote("");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
        setWorkflowBusy(false);
    };

    if (!record) {
        return <div className="py-16 text-center text-[#9A968F]"><Loader2 size={18} className="inline animate-spin mr-2" />Loading…</div>;
    }

    const activeSection = record.sections.find((s) => s.code === activeCode);
    const locked = LOCKED_STATUSES.has(record.status);
    const missingSections = nav.filter((n) => !n.is_container && n.kind !== "front_matter" && !n.not_applicable && !isNavDone(n, record.sections));

    return (
        <div className="space-y-4" data-testid="cpp-workspace-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <button data-testid="cpp-back-btn" onClick={() => navigate(-1)} className="text-[#6F6A64] hover:text-[#1F1F1F]">
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <span className="inline-block pb-0.5 text-[10px] font-bold tracking-[.08em] uppercase"
                            style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>{DOC_LABEL[record.doc_type]}</span>
                        <h2 className="text-xl font-bold tracking-tight mt-1 text-[#1F1F1F]" data-testid="cpp-title">{record.title}</h2>
                        <p className="text-[12px] text-[#9A968F]">{record.reference}
                            {record.golden_thread_item_id && (
                                <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#2E7D4F" }} data-testid="cpp-published-badge">
                                    <CheckCircle2 size={11} /> Published to Golden Thread
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canEdit && EDITABLE_STATUSES.has(record.status) ? (
                        <>
                            <select data-testid="cpp-status-select" value={record.status} onChange={(e) => updateMeta({ status: e.target.value })}
                                className={inputCls}>
                                <option value="Draft">Draft</option>
                                <option value="In progress">In progress</option>
                            </select>
                            <button data-testid="cpp-send-for-review-btn" onClick={() => runWorkflow("send-for-review")} disabled={workflowBusy}
                                className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-white text-[12.5px] font-semibold" style={{ background: "#2E6B57" }}>
                                {workflowBusy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send for review
                            </button>
                        </>
                    ) : (
                        <Pill tone={STATUS_TONE[record.status] || "neutral"}>{locked && <Lock size={10} className="inline mr-1" />}{record.status}</Pill>
                    )}
                    {canEdit && record.status === "Ready for review" && (
                        <>
                            <button data-testid="cpp-approve-btn" onClick={() => runWorkflow("approve")} disabled={workflowBusy}
                                className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-white text-[12.5px] font-semibold" style={{ background: "#2E7D4F" }}>
                                {workflowBusy ? <Loader2 size={13} className="animate-spin" /> : <ClipboardCheck size={13} />} Approve
                            </button>
                            <button data-testid="cpp-request-changes-btn" onClick={() => setReopenPrompt(true)} disabled={workflowBusy}
                                className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-[12.5px] font-semibold border" style={{ borderColor: "#D8D3CB", color: "#6F6A64" }}>
                                <RotateCcw size={13} /> Request changes
                            </button>
                        </>
                    )}
                    {canEdit && record.status === "Approved" && (
                        <>
                            <button data-testid="cpp-issue-btn" onClick={() => runWorkflow("issue")} disabled={workflowBusy}
                                className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-white text-[12.5px] font-semibold" style={{ background: "#2E7D4F" }}>
                                {workflowBusy ? <Loader2 size={13} className="animate-spin" /> : <ClipboardCheck size={13} />} Issue
                            </button>
                            <button data-testid="cpp-reopen-btn" onClick={() => setReopenPrompt(true)} disabled={workflowBusy}
                                className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-[12.5px] font-semibold border" style={{ borderColor: "#D8D3CB", color: "#6F6A64" }}>
                                <RotateCcw size={13} /> Reopen
                            </button>
                        </>
                    )}
                    {canEdit && record.status === "Issued" && (
                        <button data-testid="cpp-reopen-btn" onClick={() => setReopenPrompt(true)} disabled={workflowBusy}
                            className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-[12.5px] font-semibold border" style={{ borderColor: "#D8D3CB", color: "#6F6A64" }}>
                            <RotateCcw size={13} /> Reopen
                        </button>
                    )}
                    <button data-testid="cpp-export-btn" onClick={exportPdf} disabled={exporting}
                        className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-white text-[12.5px] font-semibold"
                        style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export PDF
                    </button>
                    {canEdit && PUBLISHABLE_STATUSES.has(record.status) && (
                        <button data-testid="cpp-publish-btn" onClick={publish} disabled={publishing}
                            className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-[12.5px] font-semibold border"
                            style={{ borderColor: "var(--brand-accent,#C75A1B)", color: "var(--brand-accent,#C75A1B)" }}>
                            {publishing ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />}
                            {record.golden_thread_item_id ? "Re-publish" : "Publish"}
                        </button>
                    )}
                </div>
            </div>

            {locked && (
                <div data-testid="cpp-locked-banner" className="flex items-center gap-2 rounded-[10px] border px-4 py-2.5 text-[12.5px] font-medium" style={{ borderColor: "#E7C9A8", background: "#FBF1E6", color: "#8A5A1E" }}>
                    <Lock size={14} /> This record is {record.status.toLowerCase()} and locked for editing. Click "Reopen" above to make further changes.
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap bg-white border rounded-xl p-3" style={{ borderColor: "#E5E0DA" }}>
                <MetaField label="Report date" testId="cpp-meta-date" type="date" value={record.report_date || ""}
                    disabled={!canEdit || locked} onSave={(v) => updateMeta({ report_date: v })} />
                <MetaField label="Prepared by" testId="cpp-meta-prepared" value={record.prepared_by || ""}
                    disabled={!canEdit || locked} onSave={(v) => updateMeta({ prepared_by: v })} />
                <MetaField label="Reviewed by" testId="cpp-meta-reviewed" value={record.reviewed_by || ""}
                    disabled={!canEdit || locked} onSave={(v) => updateMeta({ reviewed_by: v })} />
                <MetaField label="Approved by" testId="cpp-meta-approved" value={record.approved_by || ""}
                    disabled={!canEdit || locked} onSave={(v) => updateMeta({ approved_by: v })} />
            </div>

            {missingSections.length > 0 && (
                <div data-testid="cpp-missing-info-strip" className="rounded-[10px] border" style={{ borderColor: "#E8D3B0", background: "#FCF6EA" }}>
                    <button onClick={() => setMissingOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left">
                        <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold" style={{ color: "#9C6B10" }}>
                            <AlertTriangle className="w-4 h-4" /> {missingSections.length} section{missingSections.length !== 1 ? "s" : ""} still missing information
                        </span>
                        {missingOpen ? <ChevronUp className="w-4 h-4" style={{ color: "#9C6B10" }} /> : <ChevronDown className="w-4 h-4" style={{ color: "#9C6B10" }} />}
                    </button>
                    {missingOpen && (
                        <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
                            {missingSections.map((n) => (
                                <button key={n.code} data-testid={`cpp-missing-jump-${n.code}`} onClick={() => setActiveCode(n.code)}
                                    className="text-[11.5px] font-medium px-2.5 py-1 rounded-full border bg-white" style={{ borderColor: "#E8D3B0", color: "#8A5A1E" }}>
                                    {n.number} {n.title}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {(record.status_history || []).length > 0 && (
                <div data-testid="cpp-activity-panel" className="rounded-[10px] border bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <button data-testid="cpp-activity-toggle" onClick={() => setActivityOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left">
                        <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-[#4A4640]">
                            <History className="w-4 h-4" /> Activity
                        </span>
                        {activityOpen ? <ChevronUp className="w-4 h-4 text-[#9A968F]" /> : <ChevronDown className="w-4 h-4 text-[#9A968F]" />}
                    </button>
                    {activityOpen && (
                        <ul data-testid="cpp-activity-list" className="px-4 pb-3 space-y-1.5">
                            {(record.status_history || []).slice().reverse().map((h, i) => (
                                <li key={i} className="text-[12px] text-[#6F6A64]">
                                    <span className="font-semibold text-[#1F1F1F]">{h.from} → {h.to}</span> by {h.actor_name} · {new Date(h.at).toLocaleString("en-GB")}
                                    {h.note && <span className="italic"> — "{h.note}"</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <div className="flex gap-6">
                <CppSectionNav nav={nav} sections={record.sections} activeCode={activeCode} onSelect={setActiveCode} />
                {activeSection && (
                    <CppSectionEditor recordId={id} section={activeSection} numberLabel={numbering[activeSection.code]}
                        canEdit={canEdit && !locked} onSaved={onSectionSaved} />
                )}
            </div>

            {reopenPrompt && (
                <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}>
                    <div data-testid="cpp-reopen-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[440px] border" style={{ borderColor: "#E5E0DA" }}>
                        <div className="px-5 pt-4 pb-3">
                            <h3 className="font-display text-[16px] font-bold text-[#1F1F1F] mb-2">
                                {record.status === "Ready for review" ? "Request changes" : "Reopen for editing"}
                            </h3>
                            <p className="text-[12.5px] text-[#6F6A64] mb-2">This sends the record back to "In progress" so it can be edited again.</p>
                            <textarea data-testid="cpp-reopen-note" rows={3} value={reopenNote} onChange={(e) => setReopenNote(e.target.value)}
                                placeholder="Note (optional) — why is this being reopened?"
                                className="w-full px-3 py-2 rounded-lg border border-[#E5E0DA] bg-white text-[12.5px]" />
                        </div>
                        <div className="px-5 py-3.5 border-t flex items-center justify-end gap-2.5" style={{ borderColor: "#F0EDE8" }}>
                            <button onClick={() => { setReopenPrompt(false); setReopenNote(""); }} data-testid="cpp-reopen-cancel"
                                className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                            <button onClick={() => runWorkflow("reopen", { note: reopenNote || null })} disabled={workflowBusy} data-testid="cpp-reopen-confirm"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                {workflowBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function isNavDone(n, sections) {
    const s = sections.find((x) => x.code === n.code);
    if (!s) return false;
    if (s.not_applicable) return true;
    if ((s.fields || []).some((f) => f.value)) return true;
    if (s.narrative && s.narrative.trim()) return true;
    return false;
}

function MetaField({ label, testId, value, type = "text", disabled, onSave }) {
    const [v, setV] = useState(value);
    useEffect(() => setV(value), [value]);
    return (
        <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide">{label}</label>
            <input data-testid={testId} type={type} value={v} disabled={disabled}
                className="h-8 px-2.5 rounded-lg border border-[#E5E0DA] bg-white text-[12.5px] disabled:bg-[#F6F4F0] w-36"
                onChange={(e) => setV(e.target.value)} onBlur={() => onSave(v)} />
        </div>
    );
}
